/**
 * Expense List component.
 *
 * Allows adding and removing reimbursable expenses (description + amount).
 * Emits a custom 'expenses-change' event on updates.
 *
 * Exports pure helper functions for testability.
 *
 * @module expense-list
 * Requirements: 11.5
 */

import { validateExpensePayload } from '../utils/validation.js';

// ---------------------------------------------------------------------------
// Pure helper functions (exported for testing)
// ---------------------------------------------------------------------------

/**
 * Attempts to add an expense to the list.
 *
 * @param {Array<{description: string, amount: number}>} expenses - Current expenses
 * @param {string} description - Expense description
 * @param {number} amount - Expense amount
 * @returns {{ expenses: Array, error: string|null }}
 */
export function addExpense(expenses, description, amount) {
  const result = validateExpensePayload({ description, amount });
  if (!result.valid) {
    return { expenses, error: result.errors[0] };
  }
  return {
    expenses: [...expenses, { description: description.trim(), amount }],
    error: null,
  };
}

/**
 * Removes an expense at the given index.
 *
 * @param {Array<{description: string, amount: number}>} expenses - Current expenses
 * @param {number} index - Index to remove
 * @returns {Array<{description: string, amount: number}>} New expenses list
 */
export function removeExpense(expenses, index) {
  if (index < 0 || index >= expenses.length) return [...expenses];
  return expenses.filter((_, i) => i !== index);
}

/**
 * Computes the total of all expense amounts.
 *
 * @param {Array<{description: string, amount: number}>} expenses
 * @returns {number}
 */
export function computeExpenseTotal(expenses) {
  if (!Array.isArray(expenses)) return 0;
  return expenses.reduce((sum, e) => sum + (Number(e.amount) || 0), 0);
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

/**
 * Creates an Expense List component.
 *
 * @param {Object} [config={}]
 * @param {Array<{description: string, amount: number}>} [config.initialExpenses=[]]
 * @param {function} [config.onChange] - Callback when list changes: (expenses) => void
 * @returns {HTMLElement}
 */
export function createExpenseList({ initialExpenses, onChange } = {}) {
  let expenses = Array.isArray(initialExpenses) ? [...initialExpenses] : [];

  const container = document.createElement('div');
  container.className = 'expense-list';

  // Title
  const title = document.createElement('h4');
  title.className = 'expense-list__title';
  title.textContent = 'Reimbursable Expenses';
  container.appendChild(title);

  // List container
  const listEl = document.createElement('ul');
  listEl.className = 'expense-list__items';
  container.appendChild(listEl);

  // Message area
  const messageEl = document.createElement('div');
  messageEl.className = 'expense-list__message';
  messageEl.setAttribute('role', 'status');
  container.appendChild(messageEl);

  // Input row
  const inputRow = document.createElement('div');
  inputRow.className = 'expense-list__input-row';

  const descInput = document.createElement('input');
  descInput.type = 'text';
  descInput.className = 'expense-list__desc-input';
  descInput.placeholder = 'Description';
  descInput.setAttribute('aria-label', 'Expense description');
  inputRow.appendChild(descInput);

  const amountInput = document.createElement('input');
  amountInput.type = 'number';
  amountInput.className = 'expense-list__amount-input';
  amountInput.placeholder = 'Amount';
  amountInput.setAttribute('aria-label', 'Expense amount');
  amountInput.min = '0.01';
  amountInput.step = '0.01';
  inputRow.appendChild(amountInput);

  const addBtn = document.createElement('button');
  addBtn.type = 'button';
  addBtn.className = 'expense-list__add-btn';
  addBtn.textContent = 'Add';
  inputRow.appendChild(addBtn);

  container.appendChild(inputRow);

  // Total
  const totalEl = document.createElement('div');
  totalEl.className = 'expense-list__total';
  container.appendChild(totalEl);

  // --- Rendering ---

  function render() {
    listEl.innerHTML = '';
    for (let i = 0; i < expenses.length; i++) {
      const li = document.createElement('li');
      li.className = 'expense-list__item';

      const descSpan = document.createElement('span');
      descSpan.className = 'expense-list__item-desc';
      descSpan.textContent = expenses[i].description;
      li.appendChild(descSpan);

      const amountSpan = document.createElement('span');
      amountSpan.className = 'expense-list__item-amount';
      amountSpan.textContent = `£${expenses[i].amount.toFixed(2)}`;
      li.appendChild(amountSpan);

      const removeBtn = document.createElement('button');
      removeBtn.type = 'button';
      removeBtn.className = 'expense-list__remove-btn';
      removeBtn.textContent = 'Remove';
      removeBtn.setAttribute('aria-label', `Remove ${expenses[i].description}`);
      removeBtn.addEventListener('click', () => {
        expenses = removeExpense(expenses, i);
        render();
        notifyChange();
      });
      li.appendChild(removeBtn);

      listEl.appendChild(li);
    }

    totalEl.textContent = `Total: £${computeExpenseTotal(expenses).toFixed(2)}`;
  }

  function notifyChange() {
    messageEl.textContent = '';
    container.dispatchEvent(new CustomEvent('expenses-change', {
      bubbles: true,
      detail: { expenses: [...expenses] },
    }));
    if (typeof onChange === 'function') {
      onChange([...expenses]);
    }
  }

  // --- Event handlers ---

  addBtn.addEventListener('click', () => {
    const amount = parseFloat(amountInput.value);
    const result = addExpense(expenses, descInput.value, amount);
    if (result.error) {
      messageEl.textContent = result.error;
    } else {
      expenses = result.expenses;
      descInput.value = '';
      amountInput.value = '';
      messageEl.textContent = '';
      render();
      notifyChange();
    }
  });

  // Initial render
  render();

  /** Get the current expenses list. */
  container.getExpenses = () => expenses.map((e) => ({ ...e }));

  return container;
}
